/* eslint-disable no-undef */
/* eslint-disable camelcase */
/**
 * @author Fernando Ruano
 * @file index.js
 * @description Role Permissions View
 */

// Dependencies
import React from 'react';
import { Col, Row } from 'antd';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

// Components
import AppTree, { TreeItem } from '@nephro/components/AppTree';
import CardItem from '@nephro/components/CardItem';

// Helpers
import { getLastItemUrl } from '@nephro/commons/helpers/utils';

// Styles
import styles from './styles.module.css';

/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
/**
 * Maps item to tree item structure.
 * @param { Object } item - Item to map.
 * @param { Number } level - Item level.
 * @param { Object } moduleSelected - Selected module.
 * @return { Object } - Transformed item.
 */
const mapToTreeItem = (item, level, moduleSelected) => {
  const treeItemObject = {
    ...item,
    level,
    name: item.description,
    isOpen: moduleSelected?.description === item.description,
    children: item.list_childrens
      .sort((a, b) => {
        return a.order - b.order;
      })
      .map((itemSub) => mapToTreeItem(itemSub, level + 1)),
    customComponent: ({ onClick, name, isOpen, data }) => (
      <TreeItem
        onClick={onClick}
        label={name}
        isOpen={isOpen}
        data={data}
        level={level}
        // active={true}
      />
    )
  };
  return treeItemObject;
};

/**
 * Maps item to tree item structure.
 * @param { Object } item - Item to map.
 * @param { Number } level - Item level.
 * @param { Object } moduleSelected - Selected module.
 * @return { Object } - Transformed item.
 */
const mapToTreeItemAfterSelected = (
  item,
  level,
  moduleSelected,
  treeItemFull
) => {
  const entireObject = _.cloneDeep(treeItemFull);
  const indexPath = moduleSelected.keyPath.split('.');
  const objectItem = _.get(entireObject, indexPath.slice(0, -2));

  const treeItemObject = {
    ...item,
    level,
    name: item.description,
    isOpen: moduleSelected?.id === item.id,
    children: item.list_childrens
      .sort((a, b) => {
        return a.order - b.order;
      })
      .map((itemSub) =>
        mapToTreeItemAfterSelected(
          itemSub,
          level + 1,
          moduleSelected,
          treeItemFull
        )
      ),
    customComponent: ({ onClick, name, isOpen, data }) => (
      <TreeItem
        onClick={onClick}
        label={name}
        isOpen={isOpen}
        data={data}
        level={level}
        // active={true}
      />
    )
  };
  if (indexPath.length > 1 && objectItem?.id === treeItemObject.id) {
    treeItemObject.isOpen = true;
  }

  return treeItemObject;
};

/**
 * Check if an permission was selected in module
 * @param { Object } itemId - Item id.
 * @param { Object } permissionsList - Module permissions list.
 * @return { Bool } - Boolead response.
 */
const validatePermission = (itemId, permissionsList) => {
  const found = permissionsList.some(
    (element) => element.permission.permission_id === itemId
  );
  return found;
};

/**
 * Maps item to tree item structure permissions.
 * @param { Object } item - Item to map.
 * @param { Number } level - Item level.
 * @param { Object } permissionsList - Permissions list by module.
 * @return { Object } - Transformed item.
 */
const mapToTreeItemPerm = (item, level, permissionsList) => {
  const treeItemObject = {
    ...item,
    id: item.permission_id,
    level,
    name: item.name,
    isOpen: validatePermission(item.permission_id, permissionsList),
    children: [],
    customComponent: ({ onClick, name, isOpen, data }) => (
      <TreeItem
        onClick={onClick}
        label={name}
        isOpen={isOpen}
        data={data}
        level={level}
        // active={true}
      />
    )
  };
  return treeItemObject;
};
/* eslint-enable react/prop-types */
/* eslint-enable react/display-name */

let roleItemsData = [];
let treeItemsData = [];
/**
 * Role Permissions View
 * @param { Object } props - React PropTypes
 */
function RolePermissions({ menuActions, roleActions, actions }) {
  const location = useLocation();
  const [roleItems, setRoleItems] = React.useState([]);
  const [roleInfo, setRoleInfo] = React.useState([]);
  const [roleId] = React.useState(
    parseInt(getLastItemUrl(location.pathname), 10)
  );
  const [moduleSelected, setModuleSelected] = React.useState(null);
  const [permissionsList, setPermissionsList] = React.useState(null);
  const [permissionsByModule, setPermissionsByModule] = React.useState(null);

  const { getMenuByRole, getAllPermissions } = menuActions;
  const { createRolePermission, getRoleById, deleteRolePermissionById } =
    roleActions;
  const { setLoading } = actions;

  // Function to set permissions obtained from back
  const getPermissions = async (type, response) => {
    if (response.data.content) {
      setPermissionsList(
        response.data.content.map((item) => mapToTreeItemPerm(item, 0, [])),
        setLoading(false)
      );
    }
  };

  // Function to set permissions obtained from back after delete o create
  const getPermissionsAfterSelect = async (type, response) => {
    const updatedModule = _.get(roleItemsData, moduleSelected.keyPath);
    const newCurrItem = _.cloneDeep(moduleSelected);

    if (updatedModule) {
      newCurrItem.list_permissions = updatedModule?.list_permissions;
    }
    if (response.data.content) {
      setPermissionsList(
        response.data.content.map((item) =>
          mapToTreeItemPerm(item, 0, updatedModule?.list_permissions)
        ),
        setLoading(false)
      );
    }
  };

  // Function to map and set menu modules
  const getRootItems = async (type, response) => {
    if (response.data) {
      roleItemsData = response?.data?.map((item) =>
        mapToTreeItem(
          item.description === 'Pacientes'
            ? {
                ...item,
                name: 'Gestión de pacientes',
                description: 'Gestión de pacientes'
              }
            : item,
          0,
          null
        )
      );
      setRoleItems(
        response?.data?.map((item) =>
          mapToTreeItem(
            item.description === 'Pacientes'
              ? {
                  ...item,
                  name: 'Gestión de pacientes',
                  description: 'Gestión de pacientes'
                }
              : item,
            0,
            null
          )
        ),
        getAllPermissions({ paged: false }, getPermissions)
      );
    }
  };

  // Function to map and set menu modules after delete o create
  const getRootItemsAfterSelect = async (type, response) => {
    if (response.data) {
      roleItemsData = response?.data?.map((item) =>
        mapToTreeItem(
          item.description === 'Pacientes'
            ? {
                ...item,
                name: 'Gestión de pacientes',
                description: 'Gestión de pacientes'
              }
            : item,
          0,
          moduleSelected
        )
      );
      /* response?.data?.map((item) =>
        mapToTreeItemAfterSelected(item, 0, moduleSelected, treeItemsData)
      ); */
      setRoleItems(
        response?.data?.map((item) =>
          mapToTreeItemAfterSelected(
            item.description === 'Pacientes'
              ? {
                  ...item,
                  name: 'Gestión de pacientes',
                  description: 'Gestión de pacientes'
                }
              : item,
            0,
            moduleSelected,
            treeItemsData
          )
        ),
        getAllPermissions({ paged: false }, getPermissionsAfterSelect)
      );
    }
  };

  // Function to set selected module
  const onSelectItemModule = (currItem, tree) => {
    treeItemsData = tree;
    const updatedModule = _.get(roleItemsData, currItem.keyPath);
    const newCurrItem = _.cloneDeep(currItem);
    if (updatedModule.length > 0) {
      newCurrItem.list_permissions = updatedModule?.list_permissions;
    }
    setModuleSelected(newCurrItem);
    const permissionModule = updatedModule?.list_permissions;
    setPermissionsByModule(
      permissionsList.map((item) =>
        mapToTreeItemPerm(item, 0, permissionModule)
      )
    );
  };

  // Function to get menu items after delete o create role permission
  const afterSelectPerm = (moduleSelectedCopy) => {
    setModuleSelected(moduleSelectedCopy);
    getMenuByRole({ id: roleInfo.role_id }, getRootItemsAfterSelect);
  };

  // Function to set selected permission
  const onSelectItemPermission = (item) => {
    const fullSelectedPermissionItem = moduleSelected.list_permissions.filter(
      (itm) => itm.permission.permission_id === item.permission_id
    );
    const data = {
      item_menu: {
        description: moduleSelected.description,
        icon: moduleSelected.icon,
        id: moduleSelected.id,
        list_childrens: moduleSelected.list_childrens,
        order: moduleSelected.order,
        permission: moduleSelected.permission,
        url: moduleSelected.url
      },
      permission: {
        name: item.name,
        permission_id: item.permission_id
      },
      role: roleInfo
    };
    const moduleSelectedCopy = { ...moduleSelected };
    if (fullSelectedPermissionItem.length > 0) {
      deleteRolePermissionById(
        {
          id: fullSelectedPermissionItem[0]?.id_role_permission
        },
        (type) => {
          if (type === 'success') {
            if (item.name === 'All') {
              moduleSelectedCopy.list_permissions = [];
            } else {
              moduleSelectedCopy.list_permissions =
                moduleSelectedCopy.list_permissions.filter(
                  (itm) => itm.permission.permission_id !== item.permission_id
                );
            }
            afterSelectPerm(moduleSelectedCopy);
          }
        }
      );
    } else {
      createRolePermission(data, (_, response) => {
        const permission = response?.data;
        if (permission) {
          moduleSelectedCopy.list_permissions = [
            ...moduleSelectedCopy.list_permissions,
            {
              id_role_permission: response.data.role_permission_id,
              permission: data.permission
            }
          ];
          afterSelectPerm(moduleSelectedCopy);
        }
      });
    }
  };

  // Function to get and set role info
  const onGetRoleInfo = (_, response) => {
    if (response.data) {
      getMenuByRole({ id: response.data.role_id }, getRootItems);
      setRoleInfo(response.data);
    } else {
      onGoBack();
    }
  };

  React.useEffect(() => {
    getRoleById({ id: roleId }, onGetRoleInfo);
  }, []);

  React.useEffect(() => {
    roleItemsData = _.cloneDeep(roleItems);
  }, [roleItems]);

  return (
    <div className="h-full bg-secondary">
      <Row
        className="h-full"
        type="flex"
        gutter={[12, { xs: 12, md: 12, lg: 0 }]}
        align="stretch">
        <Col className="h-full" xs={24} sm={12} md={8} lg={8}>
          <CardItem className={styles.cardContainer}>
            <h1 className="font-bold text-secondary">DATOS DEL ROL</h1>
            <div className="bg-contentBackground rounded border border-grayBorders p-3">
              <div>
                <h3 className="font-semibold text-secondary">Nombre:</h3>
                <h1 className="font-normal text-justify text-baxterBlue mb-4">
                  {roleInfo.name}
                </h1>
              </div>
              <div className="mt-2">
                <h3 className="font-semibold text-secondary">Descripción:</h3>
                <h1 className="font-normal text-justify text-baxterBlue mb-4">
                  {roleInfo.description}
                </h1>
              </div>
            </div>
          </CardItem>
        </Col>
        <Col className="h-full" xs={24} sm={12} md={8} lg={8}>
          <CardItem className={styles.container}>
            <h1 className="font-bold text-secondary">
              MÓDULOS DE LA PLATAFORMA
            </h1>
            {roleItems?.length > 0 && (
              <AppTree onSelectItem={onSelectItemModule} data={roleItems} />
            )}
          </CardItem>
        </Col>
        <Col className="h-full" xs={24} sm={12} md={8} lg={8}>
          <CardItem className={styles.container}>
            <h1 className="font-bold text-secondary">PERMISOS</h1>
            {permissionsByModule && (
              <AppTree
                multipleSelection={true}
                onSelectItem={onSelectItemPermission}
                data={permissionsByModule}
                mainSelectableItem={[]}
              />
            )}
          </CardItem>
        </Col>
      </Row>
    </div>
  );
}

/**
 * @name RolePermissions.propTypes
 * @type { propTypes }
 */
RolePermissions.propTypes = {
  actions: PropTypes.shape({
    setLoading: PropTypes.func.isRequired
  }).isRequired,
  menuActions: PropTypes.shape({
    getMenuByRole: PropTypes.func.isRequired,
    getAllPermissions: PropTypes.func.isRequired
  }).isRequired,
  roleActions: PropTypes.shape({
    getRoleById: PropTypes.func.isRequired,
    createRolePermission: PropTypes.func.isRequired,
    deleteRolePermissionById: PropTypes.func.isRequired
  }).isRequired
};

/**
 * Default properties.
 */
RolePermissions.defaultProps = {};

export default RolePermissions;
